import { motion } from "motion/react"
import type { DamageZone } from "./types"
import type { Severity } from "./summarize"
import {
  VEHICLE_VIEWBOX,
  BODY_PATH,
  GLASS_PATH,
  SEAMS,
  ZONE_GEO,
  ZONE_ORDER,
} from "./vehicle-map-geometry"

// Read-only top-down silhouette for the AHA reveal: detected panels light up
// one-by-one in a stagger so the consumer watches the AI "find" the damage on
// their own car. Severity tunes the glow (emerald → amber → rose).

const FILL: Record<Severity, { fill: string; stroke: string; text: string }> = {
  minor: { fill: "rgba(16,185,129,0.40)", stroke: "#10b981", text: "#065f46" },
  moderate: { fill: "rgba(245,158,11,0.45)", stroke: "#f59e0b", text: "#92400e" },
  severe: { fill: "rgba(244,63,94,0.45)", stroke: "#f43f5e", text: "#9f1239" },
}

export function AnnotatedVehicle({
  detected,
  animate = true,
}: {
  detected: Partial<Record<DamageZone, Severity>>
  animate?: boolean
}) {
  const detectedZones = ZONE_ORDER.filter((z) => detected[z])

  return (
    <div className="relative mx-auto aspect-[4/5] w-full max-w-[320px]">
      <svg
        viewBox={VEHICLE_VIEWBOX}
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 h-full w-full"
        role="img"
        aria-label="Detected damage on your vehicle"
      >
        <defs>
          <linearGradient id="ahaBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fafafa" />
            <stop offset="1" stopColor="#e4e4e7" />
          </linearGradient>
          <linearGradient id="ahaGlass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#475569" stopOpacity="0.85" />
            <stop offset="1" stopColor="#1e293b" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {/* Body */}
        <path d={BODY_PATH} fill="url(#ahaBody)" stroke="#52525b" strokeWidth="1.4" />

        {/* Decorative chrome — mirrors + wheels, non-interactive. */}
        <g pointerEvents="none">
          <path d="M 56 38 L 50 42 Q 47 44 51 47 L 60 44 Z" fill="#e4e4e7" stroke="#71717a" strokeWidth="0.6" />
          <path d="M 164 38 L 170 42 Q 173 44 169 47 L 160 44 Z" fill="#e4e4e7" stroke="#71717a" strokeWidth="0.6" />
          <rect x="48" y="48" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
          <rect x="163" y="48" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
          <rect x="48" y="208" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
          <rect x="163" y="208" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
        </g>

        {/* Glass band */}
        <path d={GLASS_PATH} fill="url(#ahaGlass)" pointerEvents="none" />

        {/* Faint seams */}
        <g stroke="#52525b" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.5" pointerEvents="none">
          {SEAMS.map(([x1, y1, x2, y2], i) => (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
          ))}
        </g>

        {/* Detected zones — staggered reveal */}
        {detectedZones.map((zone, i) => {
          const geo = ZONE_GEO[zone]
          const sev = detected[zone]!
          const c = FILL[sev]
          const delay = animate ? 0.25 + i * 0.22 : 0
          return (
            <g key={zone}>
              <motion.path
                d={geo.d}
                fill={c.fill}
                stroke={c.stroke}
                strokeWidth="1.6"
                initial={animate ? { opacity: 0, scale: 0.9 } : false}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: `${geo.labelX}px ${geo.labelY}px` }}
              />
              <motion.text
                x={geo.labelX}
                y={geo.labelY}
                textAnchor="middle"
                fontSize={geo.rotated ? "6.5" : "7.5"}
                fontWeight="700"
                fill={c.text}
                letterSpacing="0.3"
                transform={geo.rotated ? `rotate(-90 ${geo.labelX} ${geo.labelY})` : undefined}
                pointerEvents="none"
                initial={animate ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                transition={{ delay: delay + 0.15, duration: 0.3 }}
              >
                {geo.label}
              </motion.text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
